/**
 * Settlement Step Screens — Factory-generated thin components for all step detail pages.
 *
 * Each exported component wraps SettlementStepPage with the correct step config.
 * This avoids creating 30+ nearly identical files.
 *
 * Naming: SF_{phasePrefix}_{stepNumber} → exported as SFrameNNN
 */

import { SettlementStepPage } from '../settlement';
import { SETTLEMENT_PHASE_CONFIGS } from '../../utils/settlementFlowConfig';
import type { SettlementFlowId } from '../../utils/settlementFlowMap';

function makeStepScreen(
  flowId: SettlementFlowId,
  stepId: string,
  overviewRoute: string,
  plannerFrameIds?: string[],
  options?: { statusControl?: 'none' | 'segmented'; useSettlementDocuments?: boolean }
) {
  const config = SETTLEMENT_PHASE_CONFIGS[flowId];
  const step = config.steps.find((s) => s.id === stepId);
  if (!step) {
    // Fallback: render nothing if step not found (should never happen)
    return function MissingStep() {
      return <div className="p-8 text-red-500">Step &quot;{stepId}&quot; not found in phase &quot;{flowId}&quot;</div>;
    };
  }

  const useDocs = options?.useSettlementDocuments !== false;

  return function StepScreen() {
    return (
      <SettlementStepPage
        step={step}
        phase={flowId}
        overviewRoute={overviewRoute}
        plannerFrameIds={plannerFrameIds}
        statusControl={options?.statusControl ?? 'none'}
        settlementDocumentContext={useDocs ? { phase: flowId, stepId: step.id } : undefined}
      />
    );
  };
}

/* ── Secure & Inventory Assets ── */
export { default as SFrame146 } from './secureInventory/SFrame146Accounts';
export { default as SFrame150 } from './secureInventory/SFrame150Liabilities';
export { default as SFrame155 } from './secureInventory/SFrame155Property';
export { default as SFrame160 } from './secureInventory/SFrame160Property';
export { default as SFrame162 } from './secureInventory/SFrame162Legal';
export { default as SFrame163 } from './secureInventory/SFrame163Pets';

/* ── Probate & Legal Authorities (dedicated step pages) ── */
export { default as SFrame171 } from './probate/SFrame171';
export { default as SFrame172S } from './probate/SFrame172';
export { default as SFrame173 } from './probate/SFrame173';
export { default as SFrame174S } from './probate/SFrame174';
export { default as SFrame175 } from './probate/SFrame175';
export { default as SFrame176 } from './probate/SFrame176';


/* ── Notify Interested Parties: SFrame181, 183, 186, 190, 194 are dedicated modules (Phase 5). ── */

/* ── Settle Debts, Expenses & Taxes ── */
export { default as SFrame200 } from './SFrame200';
export const SFrame201 = makeStepScreen('debts-taxes', 'dt-2', '/sframe-198', ['frame-235']);
/* SFrame200 (Verify child), 199 hub, 202, 204, 206, 207, 208–212, 214, 215: dedicated ./SFrame*.tsx */

/* ── Distribute Assets (Phase 7 — custom distribution workflow) ── */
export { default as SFrame221S } from './distribution/SFrame221';
export { default as SFrame222S } from './distribution/SFrame222';
export { default as SFrame224S } from './distribution/SFrame224';
export { default as SFrame230 } from './distribution/SFrame230';
export { default as SFrame235S } from './distribution/SFrame235';
export { default as SFrame240S } from './distribution/SFrame240';
export { default as SFrame245S } from './distribution/SFrame245';
